import { useCallback, useContext, useMemo } from 'react'
import { useIntl } from 'react-intl'
import FlightSearchWidgetStateContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateContext'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import Group from 'components/utils/Group'
import FlightSearchAirportModal from './FlightAirportSelect/FlightSearchAirportModal'
import ModalContext from 'contexts/ModalContext'
import SearchFormField from 'components/SearchV2/Components/SearchFormField/SearchFormField'
import FlightSearchMenuMobile from './FlightSearchMenu/FlightSearchMenuMobile'
import { addDays } from '@luxuryescapes/lib-refunds/build/lib/date'
import { DMY_CASUAL_FORMAT } from 'constants/dateFormats'
import SearchFormFieldGroup from 'components/SearchV2/Components/SearchFormField/SearchFormFieldGroup'
import FlightSearchTravellerSelectContent from './FlightTravellerSelect/FlightSearchTravellerSelectContent'
import { getAirportDisplayText } from 'lib/flights/flightUtils'
import useFeaturedFlightDeals from 'hooks/Flights/useFeaturedFlightDeals'
import useFlightDeals from 'hooks/Flights/useFlightDeals'
import FlightDealDuration from 'components/Flights/FlightMerchandising/FlightDealDuration/FlightDealDuration'
import FlightDealAgentHubDisclaimer from 'components/Flights/FlightMerchandising/FlightDealAgentHubDisclaimer/FlightDealAgentHubDisclaimer'
import { isAgentHubEnabled } from 'agentHub/selectors/agentHubSelectors'
import BodyText from 'LuxKit/Typography/BodyText'
import { FLIGHTS_MAX_LEAD_TIME_DAYS, FLIGHTS_MIN_LEAD_TIME_DAYS, FlightsFareTypes } from 'constants/flight'
import styled from 'styled-components'
import { rem } from 'polished'
import FlightSearchWidgetDispatchContext from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetDispatchContext'
import type { SelectingDateType } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import { FlightSearchWidgetActions } from 'contexts/Flights/FlightSearchWidget/flightSearchWidgetStateReducer'
import IconButton from 'LuxKit/Button/IconButton'
import LineArrowsSwapVerticalIcon from 'LuxKit/Icons/line/LineArrowsSwapVerticalIcon'
import { mediaQueryDown } from 'components/utils/breakpoint'
import FlightsVerticalDatePicker from './FlightsDatePicker/FlightsVerticalDatePicker'

const ButtonsContainer = styled(Group)`
  & > button:not(:last-child) {
    border-bottom: 0;
  }
`

const AirportsContainer = styled(Group)`
  position: relative;

  ${mediaQueryDown.mobile} {
    .airport-select{
      border-bottom: 0;
    }
  }

  & > button:not(.switch){
    border-bottom: 0;
  }
`

const ExchangeButtonContainer = styled.div`
  position: absolute;
  inset-inline-end: ${rem(16)};
  top: 50%;
  transform: translateY(-50%);
  border: 1px solid ${props => props.theme.palette.neutral.surface.lightForeground};
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: 50%;
`

interface Props {
  /**
   * Sets the airports to a 'read only' mode
   * Used when defaulting airport selections such as on flight deal pages
   */
  readOnlyAirports?: boolean
}

function FlightSearchWidgetMobileForm(props: Props) {
  const { readOnlyAirports } = props
  const intl = useIntl()
  const state = useContext(FlightSearchWidgetStateContext)

  const {
    fareType,
    flightDealId,
    flights,
  } = state

  const dispatch = useContext(FlightSearchWidgetDispatchContext)

  const { showSegmentActiveDeals } = state

  const flight = flights[0]
  const [flightDeals] = useFlightDeals()

  const showModal = useContext(ModalContext)

  const travellersTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'M-8hnlubARNYJJXZe8dP',
        defaultMessage: 'Travellers',
        description: 'Title for travellers selection modal in flight search widget',
      }),
    [intl],
  )

  const onTravellerSelect = useCallback(() => {
    showModal(<FlightSearchMenuMobile
      title={travellersTitle}
      showTravellers
    >
      <FlightSearchTravellerSelectContent />
    </FlightSearchMenuMobile>)
  }, [showModal, travellersTitle])

  const earliestBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MIN_LEAD_TIME_DAYS), [])
  const finalBookingDate = useMemo(() => addDays(new Date(), FLIGHTS_MAX_LEAD_TIME_DAYS), [])
  const { featuredRanges, displayDeal } = useFeaturedFlightDeals({
    flightDeals,
    departureAirport: flight.departureAirport,
    arrivalAirport: flight.arrivalAirport,
    fareType: fareType.value,
    showSegmentActiveDeals,
    flightDealId,
  })

  const dateSelectTitle = useMemo(
    () =>
      intl.formatMessage({
        id: 'C36_0JIKVCqvkVWZ8_o-',
        defaultMessage: 'When do you want to go?',
        description: 'Title for date selection modal in flight search widget',
      }),
    [intl],
  )

  const dateSelectPriceInfo = useMemo(
    () =>
      intl.formatMessage({
        id: 'i0m0YZ8D02Pv9H6IaekD',
        defaultMessage: 'Based on recent return prices (per adult) found',
        description: 'Price information text shown in date selection modal',
      }),
    [intl],
  )

  const onDateSelect = useCallback(async(selectingDateType: SelectingDateType = 'departing') => {
    // Set the selectingDateType in state before opening the modal
    dispatch({ type: FlightSearchWidgetActions.SET_ACTIVE_MENU, selectingDateType })

    const dates = await showModal<any>(<FlightSearchMenuMobile
      title={dateSelectTitle}
      showDates
    >
      <Group direction="vertical" verticalAlign="space-between" gap={12} fullHeight>
        <VerticalSpacer gap={12}>
          {displayDeal && !isAgentHubEnabled && <FlightDealDuration flightDeal={displayDeal} />}
          {isAgentHubEnabled && <FlightDealAgentHubDisclaimer />}
          <BodyText variant="small" colour="light-foreground-alt">
            {dateSelectPriceInfo}
          </BodyText>
        </VerticalSpacer>

        <FlightsVerticalDatePicker
          minDate={earliestBookingDate}
          maxDate={finalBookingDate}
          id={flight.id}
          required
          featuredRanges={featuredRanges}
        />
      </Group>
    </FlightSearchMenuMobile>,
    )

    if (dates) {
      onTravellerSelect()
    }
  }, [showModal, earliestBookingDate, finalBookingDate, flight.id, featuredRanges, displayDeal, onTravellerSelect, dateSelectTitle, dateSelectPriceInfo, dispatch])

  const onDepartingDateSelect = useCallback(() => {
    onDateSelect('departing')
  }, [onDateSelect])

  const onReturningDateSelect = useCallback(() => {
    onDateSelect('returning')
  }, [onDateSelect])

  const onArrivalSelect = useCallback(async() => {
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="arrival" id={flight.id} />,
    )
    if (airport) {
      onDateSelect('departing')
    }
  }, [onDateSelect, showModal, flight.id])

  const onDepartureSelect = useCallback(async() => {
    const airport = await showModal<App.AirportLocation | undefined>(
      <FlightSearchAirportModal direction="departure" id={flight.id} />,
    )
    if (airport) {
      onArrivalSelect()
    }
  }, [onArrivalSelect, showModal, flight.id])

  const onSwapAirports = useCallback((flightId: string) => {
    const currentFlight = flights.find(flight => flight.id === flightId)
    if (!currentFlight) return
    const departure = currentFlight.departureAirport
    dispatch({ type: FlightSearchWidgetActions.SET_DEPARTURE_AIRPORT, id: flightId, airport: currentFlight.arrivalAirport })
    dispatch({ type: FlightSearchWidgetActions.SET_ARRIVAL_AIRPORT, id: flightId, airport: departure })
  }, [flights, dispatch])

  return (
    <ButtonsContainer direction="vertical">
      <AirportsContainer direction="vertical">
        <SearchFormField
          value={getAirportDisplayText(flight.departureAirport)}
          label={intl.formatMessage({
            id: 'ZGF6ICItXlqbkxqPyUHc',
            defaultMessage: 'From',
            description: 'Label for departure airport field in flight search',
          })}
          required
          placeholder={intl.formatMessage({
            id: 'HRi6-dtcAk0WQRsHbA_y',
            defaultMessage: 'Search by city or airport',
            description: 'Placeholder text for airport search field in the search interface',
          })}
          onClick={readOnlyAirports ? undefined : onDepartureSelect}
          requiredErrorMessage={intl.formatMessage({
            id: 'XT7iAbC3Uf_TMuFN09pG',
            defaultMessage: 'Please enter a departure',
            description: 'Error message when departure airport is not selected',
          })}
          hasError={flight.showDepartureError}
          className="airport-select"
        />

        <SearchFormField
          value={getAirportDisplayText(flight.arrivalAirport)}
          label={intl.formatMessage({
            id: '2UOCctw7EHd0XUmxtgr3',
            defaultMessage: 'To',
            description: 'Form field label for arrival airport field in flight search',
          })}
          required
          placeholder={intl.formatMessage({
            id: 'HRi6-dtcAk0WQRsHbA_y',
            defaultMessage: 'Search by city or airport',
            description: 'Placeholder text for airport search field in the search interface',
          })}
          onClick={readOnlyAirports ? undefined : onArrivalSelect}
          requiredErrorMessage={intl.formatMessage({
            id: '0gkTK6pcWoE5eD7RD9w7',
            defaultMessage: 'Please enter a destination',
            description: 'Error message when destination airport is not selected',
          })}
          hasError={flight.showDestinationError}
          className="airport-select"
        />

        <ExchangeButtonContainer>
          <IconButton variant="default" kind="tertiary" size="medium" shape="circle" className="switch" onClick={() => onSwapAirports(flight.id)} disabled={readOnlyAirports}>
            <LineArrowsSwapVerticalIcon size="M" />
          </IconButton>
        </ExchangeButtonContainer>
      </AirportsContainer>

      <SearchFormFieldGroup>
        <SearchFormField
          label={intl.formatMessage({
            id: 'P7H8A2BFmIUBImUgMwMg',
            defaultMessage: 'Departing',
            description: 'Form field label for departure date field in flight search',
          })}
          placeholder={intl.formatMessage({
            id: 'Xnhjjfb2SQAvUwMEDgTX',
            defaultMessage: 'Select date',
            description: 'Placeholder text for date selection field when field is empty',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'QjCDP-dTWZM0XJzm4nhw',
            defaultMessage: 'Please select travel dates',
            description: 'Error notification displayed when travel dates are not selected',
          })}
          value={flight.checkinDate?.format(DMY_CASUAL_FORMAT)}
          onClick={onDepartingDateSelect}
          hasError={flight.showTravelDatesError}
        />

        {fareType.value === FlightsFareTypes.RETURN && <SearchFormField
          label={intl.formatMessage({
            id: '2onW6uKWgu7ObnjMNd0L',
            defaultMessage: 'Returning',
            description: 'Label for return date field in flight search in the flights flow',
          })}
          placeholder={intl.formatMessage({
            id: 'Xnhjjfb2SQAvUwMEDgTX',
            defaultMessage: 'Select date',
            description: 'Placeholder text for date selection field when field is empty',
          })}
          required
          requiredErrorMessage={intl.formatMessage({
            id: 'QjCDP-dTWZM0XJzm4nhw',
            defaultMessage: 'Please select travel dates',
            description: 'Error notification displayed when travel dates are not selected',
          })}
          value={flight.checkoutDate?.format(DMY_CASUAL_FORMAT)}
          onClick={onReturningDateSelect}
          hasError={flight.showTravelDatesError}
        />}
      </SearchFormFieldGroup>
    </ButtonsContainer>
  )
}

export default FlightSearchWidgetMobileForm
